import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import { showToast } from '../../../Components/Toast/toast';
import { FaSearch, FaPaperPlane, FaPaperclip, FaTimes, FaArrowLeft, FaUser, FaUserCircle, FaEllipsisV, FaGift } from 'react-icons/fa';

import { useGoogleTranslate } from '../../../Hooks/useGoogleTranslate';
import './Chat.css';
import chatImage from '../../../assets/images/message-chat-icon.png';
import emptyChatFigmaImage from '../../../assets/images/chat-empty-figma.png';
import heartDecor from '../../../assets/images/coin-store-figma/decor-heart.svg';
import CustomButton from '../../../Components/CustomButton';
import { getChatList, getChat, sendMessage } from '../../../Services/User/Chat';
import useUserStore from '../../../Stores/UserStore';
import useSocket from '../../../Hooks/useSocket';
import GreetingCardsModal from '../../../Components/GreetingCardsModal/GreetingCardsModal';
import { getIsUserSubscribedFromLogs, getUserSubscriptionLogs } from '../../../Services/User/UserSubscriptionService';
import { calculateTimePassed } from '../../../Utils/timeUtils';
import ReportUserModal from '../../../Components/ReportUserModal/ReportUserModal';
import BlockUserModal from '../../../Components/BlockUserModal/BlockUserModal';
import UnmatchUserModal from '../../../Components/UnmatchUserModal/UnmatchUserModal';
import PrivateContentRequestModal from '../../../Components/PrivateContentRequestModal/PrivateContentRequestModal';
import PrivateContentAccessModal from '../../../Components/PrivateContentAccessModal/PrivateContentAccessModal';

const GREETING_CARD_MESSAGE_PREFIX = '__GREETING_CARD__:';

const parseGreetingCardMessage = (messageValue) => {
  if (typeof messageValue !== 'string' || !messageValue.startsWith(GREETING_CARD_MESSAGE_PREFIX)) {
    return null;
  }

  try {
    return JSON.parse(messageValue.slice(GREETING_CARD_MESSAGE_PREFIX.length));
  } catch {
    return null;
  }
};

const createGreetingCardMessageText = (gift, quantity) => {
  return `${GREETING_CARD_MESSAGE_PREFIX}${JSON.stringify({
    id: gift?.id || null,
    title: gift?.title || gift?.card_name || gift?.gift_title || 'Greeting Card',
    description: gift?.description || '',
    image: gift?.image || gift?.img || gift?.image_url || null,
    quantity,
  })}`;
};

const getMessageSortValue = (message) => {
  if (message?.id != null && !String(message.id).startsWith('local-')) {
    return Number(message.id);
  }

  if (message?.created_at) return new Date(message.created_at).getTime();
  return new Date(`${message?.date || ''} ${message?.time || ''}`).getTime() || 0;
};

const sortMessagesChronologically = (messagesToSort) => {
  return [...messagesToSort].sort((a, b) => getMessageSortValue(a) - getMessageSortValue(b));
};

const extractChatMessageFromResponse = (response) => {
  const candidates = [
    response?.data?.message,
    response?.data?.chat_message,
    response?.data?.chatMessage,
    response?.data?.chat,
    response?.data,
    response?.chat_message,
    response?.chatMessage,
  ];

  return candidates.find((candidate) => (
    candidate &&
    typeof candidate === 'object' &&
    (
      candidate.chat_id != null ||
      candidate.message ||
      candidate.file ||
      candidate.greeting_card ||
      candidate.greetingCard ||
      candidate.card ||
      candidate.message_type === 'greeting_card' ||
      candidate.type === 'greeting_card'
    )
  ));
};

const getGreetingCardPayload = (message) => {
  if (!message) return null;

  const parsedMessage = parseGreetingCardMessage(message.message);
  if (parsedMessage) return parsedMessage;

  if (
    message.message_type === 'greeting_card' ||
    message.type === 'greeting_card' ||
    message.is_local_greeting_card ||
    message.greeting_card ||
    message.greetingCard ||
    message.card
  ) {
    return message.greeting_card || message.greetingCard || message.card || message.gift || null;
  }

  return null;
};

const Chat = () => {
  const pageTitle = useGoogleTranslate('Chat');
  const loadingChats = useGoogleTranslate('Loading chats...');
  const noChatsFound = useGoogleTranslate('No chats found');
  const upgradeToRead = useGoogleTranslate('Upgrade to read message');
  const selectChatFirst = useGoogleTranslate('Please select a chat first');
  const upgradeMembership = useGoogleTranslate('Please upgrade your membership to');
  const searchPlaceholder = useGoogleTranslate('Search chats...');
  const typeMessage = useGoogleTranslate('Type your message...');
  const send = useGoogleTranslate('Send');
  const noChatSelected = useGoogleTranslate('Select a chat to start messaging');
  const startConversation = useGoogleTranslate('Your conversations will appear here');

  const chatsLabel = useGoogleTranslate('Chats');
  const autoReplyLabel = useGoogleTranslate('Auto Reply');
  const upgradeToPremiumAutoReply = useGoogleTranslate('Please upgrade to Premium to use Auto Reply');
  const failedToLoadChats = useGoogleTranslate('Failed to load chats');
  const failedToLoadMessages = useGoogleTranslate('Failed to load messages');
  const failedToSendMessage = useGoogleTranslate('Failed to send message');
  const pleaseSelectChat = useGoogleTranslate('Please select a chat');
  const pleaseUpgradeToPlayGames = useGoogleTranslate('Please upgrade your membership to play games');
  const playGameComingSoon = useGoogleTranslate('Play game functionality - Coming soon');
  const pleaseUpgradeToSendGreetingCards = useGoogleTranslate('Please upgrade your membership to send greeting cards');
  const pleaseUpgradeToSendGifts = useGoogleTranslate('Please upgrade your membership to send gifts');
  const pleaseUpgradeToSendMessages = useGoogleTranslate('Please upgrade your membership to send messages');
  const pleaseUpgradeToUseWatchTogether = useGoogleTranslate('Please upgrade your membership to use Watch Together');
  const watchTogetherComingSoon = useGoogleTranslate('Watch together functionality - Coming soon');
  const pleaseEnterMessageOrFile = useGoogleTranslate('Please enter a message or select a file');
  const messageSentSuccessfully = useGoogleTranslate('Message sent successfully');
  const chatUserFallback = useGoogleTranslate('Chat User');
  const userFallback = useGoogleTranslate('User');
  const attachmentLabel = useGoogleTranslate('Attachment');
  const upgradeToViewAttachmentLabel = useGoogleTranslate('Upgrade to view attachment');
  const loadingMessagesLabel = useGoogleTranslate('Loading messages...');
  const noMessagesYetLabel = useGoogleTranslate('No messages yet. Start the conversation!');
  const emptyChatListTitle = useGoogleTranslate('The Chat With Users Will Appear Here');
  const emptyMainTitle = useGoogleTranslate('Find your conversations here');
  const emptyMainSubtitle = useGoogleTranslate('When someone likes your profile as well, you will be able to chat with them here.');
  const backToBrowsingLabel = useGoogleTranslate('Back To Browsing');
  const reportUserLabel = useGoogleTranslate('Report user');
  const playGameLabel = useGoogleTranslate('Play game');
  const unmatchLabel = useGoogleTranslate('Unmatch');
  const greetingCardsLabel = useGoogleTranslate('Greeting cards / Gifts');
  const blockLabel = useGoogleTranslate('Block');
  const reminderDatesLabel = useGoogleTranslate('Upcoming Dates');
  const sendRequestPrivateContentLabel = useGoogleTranslate('Send request for the private content');
  const privateContentAccessLabel = useGoogleTranslate('Add/Remove private content access');
  const watchTogetherLabel = useGoogleTranslate('Watch together');
  const activeStatusLabel = useGoogleTranslate('Active');
  const fileSelectedPrefix = useGoogleTranslate('Attachment:');
  const greetingCardPreviewLabel = useGoogleTranslate('Sent a greeting card');

  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const user = useUserStore(state => state.user);
  const socket = useSocket();

  const [searchQuery, setSearchQuery] = useState('');
  const [chatList, setChatList] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [autoReply, setAutoReply] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [attachmentImageErrors, setAttachmentImageErrors] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showGreetingCardsModal, setShowGreetingCardsModal] = useState(false);
  const [showReportUserModal, setShowReportUserModal] = useState(false);
  const [showBlockUserModal, setShowBlockUserModal] = useState(false);
  const [showUnmatchUserModal, setShowUnmatchUserModal] = useState(false);
  const [showPrivateContentRequestModal, setShowPrivateContentRequestModal] = useState(false);
  const [showPrivateContentAccessModal, setShowPrivateContentAccessModal] = useState(false);
  const [hasMessageAccess, setHasMessageAccess] = useState(null); // null = loading, true/false = from API
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const imageBaseUrl = import.meta.env.VITE_REACT_APP_IMAGE_URL;

  const resolveMediaUrl = useCallback((path) => {
    if (!path) return null;
    if (/^(https?:|data:|blob:)/i.test(path)) return path;
    return `${imageBaseUrl || ''}${path}`;
  }, [imageBaseUrl]);

  const getChatPreviewText = useCallback((lastMessage) => {
    if (typeof lastMessage === 'string') {
      return parseGreetingCardMessage(lastMessage) ? greetingCardPreviewLabel : lastMessage;
    }

    const previewText = lastMessage?.message ?? lastMessage?.text ?? '';
    if (parseGreetingCardMessage(previewText) || getGreetingCardPayload(lastMessage)) {
      return greetingCardPreviewLabel;
    }

    return previewText;
  }, [greetingCardPreviewLabel]);

  const handleImageError = (imageId) => {
    setImageErrors(prev => ({ ...prev, [imageId]: true }));
  };

  const renderAvatar = (src, alt, className, imageId) => {
    const hasError = imageErrors[imageId];
    const hasValidSrc = src && src !== 'https://via.placeholder.com/50' && src !== 'https://via.placeholder.com/40';

    if (hasError || !hasValidSrc) {
      return <FaUserCircle className={`${className} avatar-icon`} />;
    }

    return (
      <img
        src={src}
        alt={alt}
        className={className}
        onError={() => handleImageError(imageId)}
      />
    );
  };

  const fetchChatList = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await getChatList(searchQuery);
      const raw = response?.data ?? response;
      const chatListData = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      setChatList(chatListData);
    } catch (error) {
      if (!silent) {
        console.error('Error fetching chat list:', error);
        showToast(failedToLoadChats, 'error');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [searchQuery]);

  const fetchMessages = useCallback(async (chatId, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await getChat(chatId);
      const raw = response?.data ?? response;
      const messagesData = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];

      // Ensure messages are in chronological order (oldest first, newest at bottom).
      const sorted = sortMessagesChronologically(messagesData);

      setMessages(prevMessages => {
        const localGreetingCardMessages = prevMessages.filter(message => (
          message.is_local_greeting_card &&
          !sorted.some(serverMessage => {
            if (String(serverMessage.id) === String(message.id)) return true;

            const serverCard = getGreetingCardPayload(serverMessage);
            const localCard = getGreetingCardPayload(message);
            return (
              serverCard &&
              localCard &&
              String(serverCard.id || '') === String(localCard.id || '') &&
              Number(serverCard.quantity || 1) === Number(localCard.quantity || 1)
            );
          })
        ));

        if (localGreetingCardMessages.length === 0) return sorted;

        return sortMessagesChronologically([...sorted, ...localGreetingCardMessages]);
      });
    } catch (error) {
      if (!silent) {
        console.error('Error fetching messages:', error);
        showToast(failedToLoadMessages, 'error');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);
  // Debounced search effect
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchChatList();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, fetchChatList]);

  // Fetch subscription status for message access (same as play games / watch together)
  useEffect(() => {
    (async () => {
      try {
        const response = await getUserSubscriptionLogs();
        const isSubscribed = getIsUserSubscribedFromLogs(response);
        setHasMessageAccess(isSubscribed);
      } catch {
        setHasMessageAccess(false);
      }
    })();
  }, []);

  // Socket: Listen for new messages
  useEffect(() => {
    const handleNewMessage = (message) => {
      console.log('New message received:', message);

      // Add message to the list if it belongs to the current chat
      const sameChat =
        selectedChat &&
        Number(message.chat_id) === Number(selectedChat.id);

      if (sameChat) {
        setMessages(prevMessages => {
          const exists = prevMessages.some(msg => msg.id === message.id);
          if (exists) return prevMessages;

          const next = [...prevMessages, message];
          return next.sort((a, b) => {
            if (a.id != null && b.id != null) return Number(a.id) - Number(b.id);
            const aTs = a.created_at ? new Date(a.created_at).getTime() : new Date(`${a.date || ''} ${a.time || ''}`).getTime() || 0;
            const bTs = b.created_at ? new Date(b.created_at).getTime() : new Date(`${b.date || ''} ${b.time || ''}`).getTime() || 0;
            return aTs - bTs;
          });
        });
      }

      // Update chat list to show latest message
      fetchChatList();
    };

    socket.onNewMessage(handleNewMessage);

    return () => {
      socket.offNewMessage(handleNewMessage);
    };
  }, [selectedChat, socket, fetchChatList]);

  // Socket: Join/Leave chat room when chat is selected
  useEffect(() => {
    if (selectedChat) {
      fetchMessages(selectedChat.id);
      socket.joinChat(selectedChat.id);

      return () => {
        socket.leaveChat(selectedChat.id);
      };
    }
  }, [selectedChat, socket, fetchMessages]);

  // Polling fallback: refetch messages every 3s when chat is open (main chat socket may not broadcast new_message)
  useEffect(() => {
    if (!selectedChat?.id) return;

    const interval = setInterval(() => {
      fetchMessages(selectedChat.id, true);
    }, 3000);

    return () => clearInterval(interval);
  }, [selectedChat?.id, fetchMessages]);

  // Polling: refetch chat list every 3s so user list shows latest last_message
  useEffect(() => {
    const interval = setInterval(() => {
      fetchChatList(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchChatList]);

  // Socket: Listen for typing indicator
  useEffect(() => {
    const handleTyping = (data) => {
      if (selectedChat && data.chat_id === selectedChat.id && data.user_id !== user?.id) {
        setIsTyping(true);
        // Clear existing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        // Hide typing indicator after 3 seconds
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
        }, 3000);
      }
    };

    socket.onTyping(handleTyping);

    return () => {
      socket.offTyping(handleTyping);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [selectedChat, user, socket]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const scrollToBottom = () => {
    const el = messagesContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  };


  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
  };

  const handleBackToList = () => {
    setSelectedChat(null);
  };

  const handleDropdownToggle = () => {
    setShowDropdown(!showDropdown);
  };

  const handleDropdownAction = (action) => {
    setShowDropdown(false);

    switch (action) {
      case 'report':
        if (selectedChat?.user_id) {
          setShowReportUserModal(true);
        } else {
          showToast(selectChatFirst, 'error');
        }
        break;
      case 'play_game':
        (async () => {
          try {
            const response = await getUserSubscriptionLogs();
            const isSubscribed = getIsUserSubscribedFromLogs(response);

            if (!isSubscribed) {
              showToast(pleaseUpgradeToPlayGames, 'error');
              navigate('/subscription-plans');
              return;
            }

            const params = new URLSearchParams();
            if (selectedChat?.id != null) params.set('chat_id', String(selectedChat.id));
            if (selectedChat?.user_id != null) params.set('user_id', String(selectedChat.user_id));
            const query = params.toString();

            navigate(query ? `/games?${query}` : '/games', {
              state: {
                chat_id: selectedChat?.id ?? null,
                user_id: selectedChat?.user_id ?? null,
              },
            });
          } catch (error) {
            console.error('Error checking subscription before opening games:', error);
            showToast(pleaseUpgradeToPlayGames, 'error');
          }
        })();
        break;
      case 'unmatch':
        if (selectedChat?.user_id) {
          setShowUnmatchUserModal(true);
        } else {
          showToast(selectChatFirst, 'error');
        }
        break;
      case 'greeting_cards':
        (async () => {
          try {
            const response = await getUserSubscriptionLogs();
            const isSubscribed = getIsUserSubscribedFromLogs(response);

            if (!isSubscribed) {
              showToast(pleaseUpgradeToSendGreetingCards, 'error');
              navigate('/subscription-plans');
              return;
            }

            if (selectedChat?.user_id) {
              setShowGreetingCardsModal(true);
            } else {
              showToast(selectChatFirst, 'error');
            }
          } catch (error) {
            console.error('Error checking subscription before greeting cards:', error);
            showToast(pleaseUpgradeToSendGreetingCards, 'error');
          }
        })();
        break;
      case 'block':
        if (selectedChat?.user_id) {
          setShowBlockUserModal(true);
        } else {
          showToast(selectChatFirst, 'error');
        }
        break;
      case 'reminders':
        if (selectedChat?.id) {
          navigate(`/chat/${selectedChat.id}/reminders`);
        } else {
          showToast(selectChatFirst, 'error');
        }
        break;
      case 'private_content':
        if (selectedChat?.user_id) {
          setShowPrivateContentRequestModal(true);
        } else {
          showToast(selectChatFirst, 'error');
        }
        break;
      case 'private_access':
        if (selectedChat?.user_id) {
          setShowPrivateContentAccessModal(true);
        } else {
          showToast(selectChatFirst, 'error');
        }
        break;
      case 'watch_together':
        (async () => {
          try {
            const response = await getUserSubscriptionLogs();
            const isSubscribed = getIsUserSubscribedFromLogs(response);

            if (!isSubscribed) {
              showToast(pleaseUpgradeToUseWatchTogether, 'error');
              navigate('/subscription-plans');
              return;
            }

            if (selectedChat?.id) {
              navigate(`/watch-together/${selectedChat.id}`);
            } else {
              showToast(watchTogetherComingSoon, 'info');
            }
          } catch (error) {
            console.error('Error checking subscription before watch together:', error);
            showToast(pleaseUpgradeToUseWatchTogether, 'error');
          }
        })();
        break;
      default:
        break;
    }
  };

  const handleGiftClick = () => {
    (async () => {
      try {
        const response = await getUserSubscriptionLogs();
        const isSubscribed = getIsUserSubscribedFromLogs(response);

        if (!isSubscribed) {
          showToast(pleaseUpgradeToSendGifts, 'error');
          navigate('/subscription-plans');
          return;
        }

        if (selectedChat?.user_id) {
          setShowGreetingCardsModal(true);
        } else {
          showToast(selectChatFirst, 'error');
        }
      } catch (error) {
        console.error('Error checking subscription before sending gifts:', error);
        showToast(pleaseUpgradeToSendGifts, 'error');
      }
    })();
  };

  const handleGreetingCardSent = useCallback(async ({ card, quantity }) => {
    if (!selectedChat?.id) return;

    const gift = card?.gift || card || {};
    const messageText = createGreetingCardMessageText(gift, quantity);
    const formData = new FormData();
    formData.append('chat_id', selectedChat.id);
    formData.append('message', messageText);

    const chatResponse = await sendMessage(formData);
    const responseMessage = extractChatMessageFromResponse(chatResponse);
    const now = new Date();
    const fallbackMessage = {
      id: `local-greeting-card-${selectedChat.id}-${gift.id || Date.now()}-${now.getTime()}`,
      chat_id: selectedChat.id,
      user_id: user?.id,
      user_name: user?.name || user?.first_name || 'You',
      message: messageText,
      time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      created_at: now.toISOString(),
      is_local_greeting_card: true,
      message_type: 'greeting_card',
      greeting_card: {
        ...gift,
        quantity,
      },
    };

    const sentMessage = {
      ...fallbackMessage,
      ...(responseMessage || {}),
      greeting_card:
        responseMessage?.greeting_card ||
        responseMessage?.greetingCard ||
        responseMessage?.card ||
        responseMessage?.gift ||
        fallbackMessage.greeting_card,
      message: responseMessage?.message || messageText,
      message_type: responseMessage?.message_type || responseMessage?.type || 'greeting_card',
      is_local_greeting_card: !responseMessage,
    };

    setMessages(prevMessages => {
      const exists = prevMessages.some(message => String(message.id) === String(sentMessage.id));
      if (exists) return prevMessages;
      return sortMessagesChronologically([...prevMessages, sentMessage]);
    });

    socket.sendMessage({
      ...sentMessage,
      chat_id: selectedChat.id,
      message: sentMessage.message,
    });

    fetchChatList(true);
    window.setTimeout(() => {
      fetchMessages(selectedChat.id, true);
    }, 600);
  }, [fetchChatList, fetchMessages, selectedChat, socket, user]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!selectedChat) {
      showToast(pleaseSelectChat, 'error');
      return;
    }

    // Check subscription via /user/subscription before allowing to send messages
    try {
      const response = await getUserSubscriptionLogs();
      const isSubscribed = getIsUserSubscribedFromLogs(response);

      if (!isSubscribed) {
        showToast(pleaseUpgradeToSendMessages, 'error');
        navigate('/subscription-plans');
        return;
      }
    } catch (error) {
      console.error('Error checking subscription before sending messages:', error);
      showToast(pleaseUpgradeToSendMessages, 'error');
      return;
    }

    if (!messageText.trim() && !selectedFile) {
      showToast(pleaseEnterMessageOrFile, 'error');
      return;
    }

    try {
      setSending(true);

      // Stop typing indicator
      socket.emitStopTyping(selectedChat.id);

      const formData = new FormData();
      formData.append('chat_id', selectedChat.id);
      formData.append('message', messageText);

      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await sendMessage(formData);
      // Add new message to the list if response contains data
      const messageData = response?.data ?? response?.detail ?? response;
      const msg = messageData?.data ?? messageData;
      if (msg && (msg.id != null || msg.message)) {
        setMessages(prevMessages => {
          const exists = msg.id != null && prevMessages.some(m => m.id === msg.id);
          if (exists) return prevMessages;
          return [...prevMessages, msg];
        });
        socket.sendMessage({
          chat_id: selectedChat.id,
          message: msg
        });
      }

      // Clear input and file
      setMessageText('');
      handleRemoveFile();

      showToast(messageSentSuccessfully, 'success');
    } catch (error) {
      console.error('Error sending message:', error);
      showToast(failedToSendMessage, 'error');
    } finally {
      setSending(false);
    }
  };

  const handleMessageInput = (e) => {
    const value = e.target.value;
    setMessageText(value);

    // Emit typing indicator
    if (value.trim() && selectedChat) {
      socket.emitTyping(selectedChat.id);

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        socket.emitStopTyping(selectedChat.id);
      }, 2000);
    }
  };

  const renderChatList = () => {
    if (loading && chatList.length === 0) {
      return (
        <div className="text-center py-4">
          <p className="text-muted">{loadingChats}</p>
        </div >
      );
    }

    if (chatList.length === 0) {
      return (
        <div className="text-center py-4">
          <p className="text-muted">{noChatsFound}</p>
        </div >
      );
    }

    return chatList.map((chat) => (
      <div
        key={chat.id}
        className={`chat-list-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
        onClick={() => handleSelectChat(chat)}
      >
        <div className="chat-avatar">
          {renderAvatar(
            chat.image ? `${imageBaseUrl}${chat.image}` : null,
            chat.name || chatUserFallback,
            'chat-avatar-img',
            `chat-${chat.id}`
          )}
          {chat.unread > 0 && <span className="unread-badge">{chat.unread}</span>}
        </div>
        <div className="chat-info">
          <h6 className="chat-name">{chat.name || `Chat #${chat.id}`}</h6>
          <p className="chat-preview">
            {hasMessageAccess
              ? (getChatPreviewText(chat.last_message) || chat.latest_message || '—')
              : upgradeToRead}
          </p >
        </div >
        <div className="chat-meta">
          <span className="chat-time">
            {chat.last_message?.read_at
              ? calculateTimePassed(chat.last_message.read_at)
              : chat.last_message?.date && chat.last_message?.time
                ? calculateTimePassed(`${chat.last_message.date}T${chat.last_message.time}`)
                : '—'}
          </span>
        </div>
      </div >
    ));
  };

  const renderMessages = () => {
    if (!selectedChat) {
      return (
        <div className="empty-chat-state">
          <img src={chatImage} alt="Chat" className="empty-state-image" />
          <p className="empty-state-text">{emptyChatListTitle}</p>
        </div >
      );
    }

    if (loading && messages.length === 0) {
      return (
        <div className="empty-chat-state">
          <p className="text-muted">{loadingMessagesLabel}</p>
        </div >
      );
    }

    if (messages.length === 0) {
      return (
        <div className="empty-chat-state">
          <img src={chatImage} alt="Chat" className="empty-state-image" />
          <p className="empty-state-text">{noMessagesYetLabel}</p>
        </div >
      );
    }

    return (
      <div className="messages-container">
        {messages.filter(msg => msg && msg.id).map((message) => {
          const isSent = message.user_id === user?.id;
          const isReceived = !isSent;
          const greetingCard = getGreetingCardPayload(message);
          const greetingCardImage = greetingCard
            ? resolveMediaUrl(
              greetingCard.image?.path ||
              (typeof greetingCard.image === 'string' ? greetingCard.image : null) ||
              greetingCard.img ||
              greetingCard.image_url
            )
            : null;
          const greetingCardTitle = greetingCard?.title || greetingCard?.card_name || greetingCard?.gift_title || 'Greeting Card';
          const greetingCardQuantity = Number(greetingCard?.quantity || 1);
          const greetingCardImageId = `greeting-card-${message.id}`;
          const hasGreetingCardImageError = attachmentImageErrors[greetingCardImageId];
          const displayMessageText = parseGreetingCardMessage(message.message)
            ? ''
            : message.message;

          return (
            <div
              key={message.id}
              className={`message ${isSent ? 'sent' : 'received'}`}
            >
              {isReceived && (
                <div className="message-avatar-wrapper">
                  {renderAvatar(
                    message.image ? `${imageBaseUrl}${message.image}` : null,
                    message.user_name || userFallback,
                    'message-avatar',
                    `message-${message.id}`
                  )}
                </div>
              )}
              <div className="message-content">
                {isReceived && (
                  <p className="message-sender">{message.user_name || 'User'}</p>
                )}
                {greetingCard && (
                  <div className="message-greeting-card">
                    {greetingCardImage && !hasGreetingCardImageError && (
                      <img
                        src={greetingCardImage}
                        alt={greetingCardTitle}
                        className="message-greeting-card-image"
                        onClick={() => window.open(greetingCardImage, '_blank')}
                        onError={() => {
                          setAttachmentImageErrors(prev => ({ ...prev, [greetingCardImageId]: true }));
                        }}
                      />
                    )}
                    <div className="message-greeting-card-info">
                      <span className="message-greeting-card-label">Greeting Card</span>
                      <strong>{greetingCardTitle}</strong>
                      {greetingCardQuantity > 1 && (
                        <span className="message-greeting-card-quantity">Qty: {greetingCardQuantity}</span>
                      )}
                    </div>
                  </div>
                )}
                {message.file && (() => {
                  // Handle file object with path property or string
                  const filePath = typeof message.file === 'string'
                    ? `${imageBaseUrl}${message.file}`
                    : message.file.path || (message.file.url ? message.file.url : null);

                  if (!filePath) return null;

                  const fileType = typeof message.file === 'string'
                    ? null
                    : message.file.type || null;

                  const attachmentId = `attachment-${message.id}`;
                  const hasError = attachmentImageErrors[attachmentId];

                  // Check if it's an image type
                  const isImage = fileType && ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(fileType.toLowerCase());

                  // Also check by file extension if type is not available
                  const isImageByExtension = filePath && /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(filePath);

                  if ((isImage || isImageByExtension) && !hasError) {
                    return (
                      <div className="message-file message-image-preview">
                        <img
                          src={filePath}
                          alt="Attachment"
                          className="message-attachment-image"
                          onClick={() => window.open(filePath, '_blank')}
                          onError={() => {
                            setAttachmentImageErrors(prev => ({ ...prev, [attachmentId]: true }));
                          }}
                        />
                      </div>
                    );
                  } else {
                    return (
                      <div className="message-file">
                        <a href={filePath} target="_blank" rel="noopener noreferrer" className="message-file-link">
                          📎 {hasError ? 'View Attachment' : 'Attachment'}
                        </a>
                      </div>
                    );
                  }
                })()}
                {displayMessageText && (
                  <p className="message-text">{String(displayMessageText)}</p>
                )}
                <span className="message-time">{message.time || ''}</span>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
    );
  };

  return (
    <section className="chat-figma-page">
      <img src={heartDecor} alt="" className="chat-figma-decor-heart" aria-hidden="true" />
      <div className="chat-figma-hero">
        <Container>
          <h1>{pageTitle}</h1>
        </Container>
      </div>
      <div className="chat-figma-main py-sm-5 py-4">
        <Container className="chat-figma-container">
          <div className="chat-container">
            {/* Chat List Sidebar */}
            <div className={`chat-sidebar ${selectedChat ? 'has-selected-chat' : ''}`}>
              <div className="chat-sidebar-header">
                <h4>{chatsLabel}</h4>
                <button
                  className={`auto-reply-btn ${autoReply ? 'active' : ''}`}
                  onClick={async () => {
                    try {
                      const response = await getUserSubscriptionLogs();
                      const isSubscribed = getIsUserSubscribedFromLogs(response);

                      if (isSubscribed) {
                        navigate('/auto-reply');
                      } else {
                        showToast(upgradeToPremiumAutoReply, 'error');
                        navigate('/subscription-plans');
                      }
                    } catch (error) {
                      console.error('Error checking subscription before auto reply:', error);
                      showToast(upgradeToPremiumAutoReply, 'error');
                      navigate('/subscription-plans');
                    }
                  }}
                >
                  {autoReplyLabel}
                </button>
              </div >

              <div className="chat-search">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>

              <div className="chat-list">
                {renderChatList()}
              </div>
            </div >

            {/* Chat Messages Area */}
            < div className={`chat-main ${selectedChat ? 'has-selected-chat' : ''}`}>
              {
                selectedChat ? (
                  <>
                    {/* Chat Header */}
                    < div className="chat-main-header" >
                      <button className="back-to-list-btn" onClick={handleBackToList}>
                        <FaArrowLeft />
                      </button>
                      <div className="chat-header-user">
                        {renderAvatar(
                          selectedChat.image ? `${imageBaseUrl}${selectedChat.image}` : null,
                          selectedChat.name || userFallback,
                          'chat-header-avatar',
                          `header-${selectedChat.id}`
                        )}
                        <div className="chat-header-info">
                          <h6 className="chat-header-name">{selectedChat.name || `Chat #${selectedChat.id}`}</h6>
                          <span className="chat-header-status">{activeStatusLabel}</span>
                        </div >
                      </div >

                      {/* Header Actions */}
                      < div className="chat-header-actions" >
                        <button className="chat-action-btn" onClick={handleGiftClick}>
                          <FaGift />
                        </button>

                        <div className="chat-dropdown-wrapper" ref={dropdownRef}>
                          <button className="chat-action-btn" onClick={handleDropdownToggle}>
                            <FaEllipsisV />
                          </button>

                          {
                            showDropdown && (
                              <div className="chat-dropdown-menu">
                                <button className="dropdown-item" onClick={() => handleDropdownAction('greeting_cards')}>
                                  {greetingCardsLabel}
                                </button>
                                <button className="dropdown-item" onClick={() => handleDropdownAction('play_game')}>
                                  {playGameLabel}
                                </button>
                                <button className="dropdown-item" onClick={() => handleDropdownAction('watch_together')}>
                                  {watchTogetherLabel}
                                </button>
                                <button className="dropdown-item" onClick={() => handleDropdownAction('private_content')}>
                                  {sendRequestPrivateContentLabel}
                                </button>
                                <button className="dropdown-item" onClick={() => handleDropdownAction('reminders')}>
                                  {reminderDatesLabel}
                                </button>
                                <button className="dropdown-item" onClick={() => handleDropdownAction('private_access')}>
                                  {privateContentAccessLabel}
                                </button>
                                <button className="dropdown-item" onClick={() => handleDropdownAction('unmatch')}>
                                  {unmatchLabel}
                                </button>
                                <button className="dropdown-item" onClick={() => handleDropdownAction('block')}>
                                  {blockLabel}
                                </button>
                                <button className="dropdown-item" onClick={() => handleDropdownAction('report')}>
                                  {reportUserLabel}
                                </button>
                                
                                
                                
                                
                                
                              </div>
                            )}
                        </div>
                      </div >
                    </div >

                    <div ref={messagesContainerRef} className="chat-messages">
                      {renderMessages()}
                      {isTyping && (
                        <div className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      )}
                    </div>

                    {/* Message Input */}
                    <div className="chat-input-container">
                      {selectedFile && (
                        <div className="selected-file">
                          <span>📎 {fileSelectedPrefix} {selectedFile.name}</span>
                          <button onClick={handleRemoveFile} className="remove-file-btn">
                            <FaTimes />
                          </button>
                        </div >
                      )
                      }
                      <form onSubmit={handleSendMessage} className="chat-input-form">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="attach-btn"
                        >
                          <FaPaperclip />
                        </button>
                        <input
                          type="text"
                          placeholder={typeMessage}
                          value={messageText}
                          onChange={handleMessageInput}
                          className="message-input"
                          disabled={sending}
                        />
                        <button type="submit" className="send-btn" disabled={sending}>
                          <FaPaperPlane />
                        </button>
                      </form>
                    </div >
                  </>
                ) : (
                  <div className="empty-chat-state empty-chat-state-figma">
                    <img src={emptyChatFigmaImage} alt="Chat" className="empty-state-image empty-state-image-figma" />
                    <p className="empty-state-text empty-state-text-figma">{emptyChatListTitle}</p>
                    <div className="empty-main-extra">
                      <h5 className="empty-state-title">{emptyMainTitle}</h5>
                      <p className="empty-state-subtitle">
                        {emptyMainSubtitle}
                      </p>
                      <CustomButton className="mt-3" onClick={() => navigate('/home')}>
                        {backToBrowsingLabel}
                      </CustomButton>
                    </div>
                  </div >
                )}
            </div >
          </div >
        </Container >
      </div>

      {/* Greeting Cards Modal */}
      < GreetingCardsModal
        show={showGreetingCardsModal}
        onHide={() => setShowGreetingCardsModal(false)}
        userId={selectedChat?.user_id}
        onSent={handleGreetingCardSent}
      />
      {/* Report User Modal */}
      < ReportUserModal
        show={showReportUserModal}
        onHide={() => setShowReportUserModal(false)}
        userName={selectedChat?.name || `Chat #${selectedChat?.id}`}
        userId={selectedChat?.user_id}
      />
      {/* Block User Modal */}
      < BlockUserModal
        show={showBlockUserModal}
        onHide={() => setShowBlockUserModal(false)}
        userName={selectedChat?.name || `Chat #${selectedChat?.id}`}
        userId={selectedChat?.user_id}
      />
      {/* Unmatch User Modal */}
      < UnmatchUserModal
        show={showUnmatchUserModal}
        onHide={() => setShowUnmatchUserModal(false)}
        userName={selectedChat?.name || `Chat #${selectedChat?.id}`}
        userId={selectedChat?.user_id}
      />
      {/* Private Content Request Modal */}
      < PrivateContentRequestModal
        show={showPrivateContentRequestModal}
        onHide={() => setShowPrivateContentRequestModal(false)}
        userName={selectedChat?.name || `Chat #${selectedChat?.id}`}
      />
      {/* Private Content Access Modal */}
      <PrivateContentAccessModal
        show={showPrivateContentAccessModal}
        onHide={() => setShowPrivateContentAccessModal(false)}
        userName={selectedChat?.name || `Chat #${selectedChat?.id}`}
        userId={selectedChat?.user_id}
      />
    </section>
  );
};

export default Chat;
